import { useCallback, useEffect, useRef, useState } from "react";
import { OrgChart } from "d3-org-chart";
import * as d3 from "d3";
import ReactDOMServer from "react-dom/server";
import HierarchyBulkFilter from "./HierarchyBulkFilter";
import CustomLoader from "components/CustomLoader";
import NsaHierarchyOrgChartButton from "../NSA/NSAHierarchyOrgCharButton";
import HierarchyPdfOrgChartNode from "./HierarchyPdfOrgChartNode";

export default function HierarchyOrgChartPdfView(chart) {
  const [count, setCount] = useState(0);
  const setDownloading = chart.downloading;
  const setShow = chart.show;
  const pdf = chart.pdf;
  const data = chart.chart;
  const title = chart.title;
  const setCompactChart = chart.compact;

  let list = useCallback(HierarchyBulkFilter(data));
  let items = [];

  list.sections.forEach((value, key) => {
    items.push(key);
  });

  const containerStyles = {
    width: "60%",
    height: "100%",
  };

  const [root, setRoot] = useState(true);
  const d3JSContainer = useRef(null);
  const chartData = useRef(null);

  useEffect(() => {
    if (data && d3JSContainer.current) {
      chartData.current = new OrgChart();
      const chart = chartData.current;
      let children = 0;
      let rootDirectChildren = false;
      if (root) {
        rootDirectChildren = true;
      }
      chart
        .container(d3JSContainer.current)
        .data(root ? list.hierarchyType : list.sections[list.order[count - 1]])
        .linkUpdate(function () {
          d3.select(this).attr("stroke", "#ced4da").attr("stroke-width", 2);
        })
        .nodeWidth((d) => {
          const hasSubtitle = d.data.parentId !== "";
          return hasSubtitle ? 230 : 230;
        })
        .nodeHeight((d) => {
          const baseHeight = 105;
          return baseHeight;
        })
        .childrenMargin((d) => 120)
        .compactMarginBetween((d) => 100)
        .compactMarginPair((d) => 90)
        .siblingsMargin((d) => 80)
        .buttonContent(({ node }) => {
          if (children < node.data._directSubordinates) {
            if (root && rootDirectChildren) {
              children =
                list.hierarchyType[list.hierarchyType.length - 1]
                  ._directSubordinates;
            } else {
              children = node.data._directSubordinates;
            }
          }
          return ReactDOMServer.renderToString(
            <NsaHierarchyOrgChartButton
              children={node.children}
              directSubordinates={node.data._directSubordinates}
            />
          );
        })
        .nodeContent((node) => {
          return ReactDOMServer.renderToString(
            <HierarchyPdfOrgChartNode data={node} />
          );
        })
        .compact(true);

      if (root) {
        chart.render();
        setRoot(false);
      } else {
        chart.expandAll().render();
      }
      download(children);
    }
  }, [count]);

  //#region Hierarchy org chart download
  function download(children) {
    chartData.current.exportImg({
      save: false,
      full: true,
      scale:
        (!root && list.sections[list.order[count - 1]]?.length >= 50) ||
        (root && list.hierarchyType?.length >= 50)
          ? 15
          : 10,

      onLoad: (base64) => {
        let img = new Image();
        img.src = base64;
        img.onload = function () {
          let { width, height } = img;
          if (count !== 0) {
            pdf.addPage();
          }

          const pdfWidth = pdf.internal.pageSize.getWidth();
          const pdfHeight = pdf.internal.pageSize.getHeight();

          // Calculate scaling factors
          const scaleWidth = pdfWidth / width;
          const scaleHeight = pdfHeight / height;

          // Use the higher scaling factor to fit the image within the page
          const scale = Math.max(scaleWidth, scaleHeight);

          // Calculate new dimensions
          const newWidth =
            (root && children <= 8 && list.hierarchyType?.length <= 50) ||
            (children <= 8 &&
              list.sections[list.order[count - 1]]?.length <= 50)
              ? children <= 3
                ? (width * scale) / 2.5
                : (width * scale) / 2
              : children <= 8
              ? children <= 3
                ? (width * scale) / 2.5
                : (width * scale) / 2
              : width * scale;
          const newHeight =
            (root && children <= 8 && list.hierarchyType.length <= 50) ||
            (children <= 8 && list.sections[list.order[count - 1]].length <= 50)
              ? children <= 3
                ? (height * scale) / 2.5
                : (height * scale) / 2
              : children <= 8
              ? children <= 3
                ? (width * scale) / 2.5
                : (width * scale) / 2.5
              : height * scale;

          // Center the image on the page
          const x = (pdfWidth - newWidth) / 2;
          const y = (pdfHeight - newHeight) / 2;

          // Add the image to the PDF
          pdf.addImage(
            img,
            "PNG",
            x, // X-coordinate
            y, // Y-coordinate
            newWidth, // Scaled width
            newHeight, // Scaled height
            count,
            "FAST" // Use high-quality compression
          );

          if (count === items.length) {
            pdf.save(title);
            setDownloading(false);
            setCompactChart(false);
            setShow(false);
            window.location.reload();
          } else {
            setCount(count + 1);
          }
        };
      },
    });
  }
  //#endregion

  return (
    <>
      <div>
        <CustomLoader />
        <div className="text-center">
          <h3>Downloading</h3>
          {count + 1} of {items.length + 1}
        </div>
      </div>
      <div
        style={{
          overflow: "hidden",
          height: "0px",
        }}
      >
        <div style={containerStyles} ref={d3JSContainer} className="relative" />
      </div>
    </>
  );
}
